import React, { useCallback } from 'react';

import { Card, CardContent } from '@material-ui/core';
import { updatePostAction, toggleAddPostModal } from 'actions/addPost';
import DotsMoreComponent, { getPostMoreMenuItems } from 'components/Comment/CommentMore';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { postDataInitialStateRabbit } from 'reducers/addPost';
import { selectFeedDataById } from 'selectors/feed';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { FeedCardMobileCSS } from 'styles/common';

import { LANDING_PAGE_TYPES, SENTIMENTS, TASK_TYPES_2, OKR_TYPES } from 'constants/ProjectConstants';
import { getObjectiveDetailsRoute, getProfileRoute, getTagDetailRoute, getTeamPageRoute } from 'constants/helper';
import { sendClickedPostEvent } from 'constants/mixpanelHelper';

import { showTaskDetails } from 'services/taskDetails';

import { TextParseComponent } from 'uiToolkitV1/TextParseComponent';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import CardHeaderWithBorder from './CardHeaderWithBorder';
import KudosAndFeedbackPostFeedItem from './KudosPostFeedItem';
import { CardWrapper } from './common/FeedCard';

export function PostHeader({
  fromEmployee,
  Stamp,
  createdDate,
  border = true,
  visibilityType,
  sentimentType,
  isFeedbackHeader = false,
}) {
  const history = useHistory();
  function goToProfilePage(employeeId) {
    history.push(getProfileRoute(employeeId));
  }
  return (
    <StyledCardHeaderWithBorder
      $isFeedbackHeader={isFeedbackHeader}
      imgSrc={fromEmployee.getProfileImage()}
      stamp={Stamp}
      // stamp={getVisibilityStamp(data.postRes && data.postRes.visibilityType)}
      title={fromEmployee.fullName}
      createdDate={createdDate}
      goToProfilePage={goToProfilePage.bind(null, fromEmployee.id)}
      border={border}
      postVisibility={visibilityType}
      postSentiment={sentimentType}
    />
  );
}

export function PostText({ className, text, macrosRes, onPostClick, ...textStyleProps }) {
  return (
    <TextBodyMedium
      className={className}
      sx={{
        marginBottom: '8px',
        span: {
          color: '#333333',
        },
      }}
      onClick={onPostClick}
      {...textStyleProps}
    >
      <TextParseComponent text={text} macros={macrosRes} color={textStyleProps?.customColor} />
      {/* <StrengthsHashTags strengths={skills} /> */}
    </TextBodyMedium>
  );
}

// A separate parent Component named FeedItem would be made which has
// all the modular logic that we have almost built in this component
/**
 * @param {PostFeedItemData} data
 */
function PostFeedItem(props) {
  const {
    feedId,
    className,
    employee,
    pageType,
    renderLikeCommentsSectionComponent,
    ReasonHeader, // TODO: Later individual renders like this need to come as children, so that there is no hard binding like this
    MediaView,
  } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);
  const data = useSelector((state) => selectFeedDataById(state, feedId)) || {};
  const {
    landingPage: landingPageDetails,
    fromEmployee,
    text,
    macrosRes,
    id,
    createdDate,
    media = [],
    contentState,
    actions,
    visibilityType,
    feedback = false,
  } = data;

  const landingPageId = landingPageDetails?.ids?.[0];

  // Extract it out to separate custom hook
  const onPostClick = useCallback(
    (event) => {
      if (!landingPageDetails.ids || !landingPageDetails.ids.length === 0) return;
      // if bubbling is happeing and a child link is clicked then don't open panel
      sendClickedPostEvent();
      if (event.target && event.target.tagName === 'A') return;
      if (landingPageDetails?.type?.[landingPageId] === TASK_TYPES_2.task) {
        dispatch(showTaskDetails({ taskIds: landingPageDetails.ids, employee, type: TASK_TYPES_2.task }));
      } else if (landingPageDetails?.type?.[landingPageId] === TASK_TYPES_2.goal) {
        dispatch(showTaskDetails({ taskIds: landingPageDetails.ids, employee, type: TASK_TYPES_2.goal }));
      } else if (landingPageDetails?.type?.[landingPageId] === OKR_TYPES.OBJECTIVE) {
        const route = getObjectiveDetailsRoute({
          location,
          objectivePId: landingPageId,
          useCurrentRoute: true,
        });
        history.push(route);
      } else if (landingPageDetails.type === LANDING_PAGE_TYPES.team) {
        const teamId = landingPageDetails.ids[0];
        const teamName = landingPageDetails.data[teamId].name;
        history.push(getTeamPageRoute({ teamId, teamName }));
      } else if (landingPageDetails.type === LANDING_PAGE_TYPES.tag) {
        const tagId = landingPageDetails.ids[0];
        const tagName = landingPageDetails.data[tagId].name;
        history.push(getTagDetailRoute({ tagId, tagName }));
      }
    },
    [dispatch, JSON.stringify(landingPageDetails), history, pageType, JSON.stringify(employee)]
  );

  if (!Object.keys(data) === 0) return;

  const editActionData = actions.edit;

  function onEditClick() {
    const initialState = postDataInitialStateRabbit(
      contentState,
      media,
      id,
      visibilityType.name,
      macrosRes?.attributes?.sentiment
    );
    dispatch(updatePostAction(initialState));
    dispatch(toggleAddPostModal());
  }

  const isKudosPost = feedback && macrosRes?.attributes?.sentiment === SENTIMENTS.appreciative;
  const isFeedbackPost = feedback && macrosRes?.attributes?.sentiment === SENTIMENTS.constructive;

  if (isKudosPost || isFeedbackPost) return <KudosAndFeedbackPostFeedItem isKudos={isKudosPost} {...props} />;

  return (
    <CardWrapper>
      <StyledCard className={className || ''} elevation={0}>
        {typeof ReasonHeader === 'function' ? ReasonHeader({ onClick: onPostClick }) : ReasonHeader}
        <PostHeader
          fromEmployee={fromEmployee}
          loggedInEmployeeId={loggedInEmployeeId}
          Stamp={
            loggedInEmployeeId === fromEmployee.id && (
              <DotsMoreComponent
                menuItems={getPostMoreMenuItems({
                  toggleEditing: onEditClick,
                  isEditable: editActionData.canPerform,
                  cantEditReason: editActionData.reason,
                  visibilityType,
                })}
              />
            )
          }
          visibilityType={data.visibilityType?.name}
          sentimentType={macrosRes?.attributes?.sentiment}
          createdDate={createdDate}
        />
        <StyledCardContent>
          <PostText text={text} macrosRes={macrosRes} onPostClick={onPostClick} />
          <MediaView />
        </StyledCardContent>
        {renderLikeCommentsSectionComponent()}
      </StyledCard>
    </CardWrapper>
  );
}

const StyledCard = styled(Card)`
  ${FeedCardMobileCSS};
  overflow: inherit; // to make sure that the smileys are visible outside the parent content as well
`;

const StyledCardContent = styled(CardContent)`
  &.MuiCardContent-root {
    padding: 0.7rem 1.5rem 0;
  }
  // & > * {
  //   margin-bottom: ${(props) => props.theme.spacing(1)}rem;
  // }
`;

const StyledCardHeaderWithBorder = styled(CardHeaderWithBorder)`
  ${(props) =>
    props.$isFeedbackHeader &&
    css`
      padding-left: 0rem;
    `};
`;

export default PostFeedItem;

// function getVisibilityStamp(visibilityType) {
//   if (visibilityType) {
//     let tooltip, icon;
//     if (visibilityType === POST_VISIBILITY_TYPES.global.name) {
//       tooltip = 'Visibile to everyone';
//       icon = <PublicIcon />;
//     } else {
//       tooltip = 'Only visible to tagged employees';
//       icon = <LockOutlinedIcon />;
//     }
//     return (
//       <Tooltip title={tooltip}>
//         <div style={{ paddingTop: '1rem', paddingRight: '1rem' }}>
//           <StyledIcon size="header" color="textSecondary">
//             {icon}
//           </StyledIcon>
//         </div>
//       </Tooltip>
//     );
//   }
//   return null;
// }
